import { useCallback, useState } from 'react';

import { apiService } from '../services/api';
import savedContentService from '../services/savedContentService';
import type { TableItem } from '../services/driveService';
import type { ApiDocument } from '../services/api';

export interface UseDriveBulkActionsParams {
  deletableItems: TableItem[];
  reprocessableItems: TableItem[];
  loadPrivateDocuments: () => Promise<void>;
  loadSharedDocuments: () => Promise<void>;
  loadSavedData: () => Promise<void>;
  setPrivateDocuments: React.Dispatch<React.SetStateAction<any[]>>;
  setSharedDocuments: React.Dispatch<React.SetStateAction<any[]>>;
  setSavedMessages: React.Dispatch<React.SetStateAction<any[]>>;
  setSnippets: React.Dispatch<React.SetStateAction<any[]>>;
  setStudioResults: React.Dispatch<React.SetStateAction<any[]>>;
  setInternalSelectedDocuments: React.Dispatch<React.SetStateAction<string[]>>;
  onDocumentDelete?: (document: ApiDocument) => Promise<void> | void;
}

export const useDriveBulkActions = ({
  deletableItems,
  reprocessableItems,
  loadPrivateDocuments,
  loadSharedDocuments,
  loadSavedData,
  setPrivateDocuments,
  setSharedDocuments,
  setSavedMessages,
  setSnippets,
  setStudioResults,
  setInternalSelectedDocuments,
  onDocumentDelete,
}: UseDriveBulkActionsParams) => {
  const [isBulkActionLoading, setIsBulkActionLoading] = useState(false);
  const [bulkActionMessage, setBulkActionMessage] = useState<string | null>(null);

  const refreshDriveData = useCallback(async () => {
    await Promise.allSettled([loadPrivateDocuments(), loadSharedDocuments(), loadSavedData()]);
  }, [loadPrivateDocuments, loadSharedDocuments, loadSavedData]);

  const clearLocalState = useCallback(() => {
    setPrivateDocuments((prev) =>
      prev.filter((doc) => !deletableItems.some((item) => item.id === doc.id))
    );
    setSharedDocuments((prev) =>
      prev.filter((doc) => !deletableItems.some((item) => item.id === doc.id))
    );
    setSavedMessages((prev) =>
      prev.filter((msg) => !deletableItems.some((item) => item.id === msg.id))
    );
    setSnippets((prev) =>
      prev.filter((snippet) => !deletableItems.some((item) => item.id === snippet.id))
    );
    setStudioResults((prev) =>
      prev.filter((result) => !deletableItems.some((item) => item.id === result.id))
    );
    setInternalSelectedDocuments((prev) =>
      prev.filter((id) => !deletableItems.some((item) => item.id === id))
    );
  }, [
    deletableItems,
    setInternalSelectedDocuments,
    setPrivateDocuments,
    setSavedMessages,
    setSharedDocuments,
    setSnippets,
    setStudioResults,
  ]);

  const handleBulkDelete = useCallback(async () => {
    if (!deletableItems.length) return;
    // Confirmación removida (window.confirm bloquea MCP automation)

    setIsBulkActionLoading(true);
    try {
      const errors: string[] = [];

      for (const item of deletableItems) {
        try {
          if (item.type === 'document') {
            await onDocumentDelete?.(item.data as ApiDocument);
          } else if (item.type === 'saved-message') {
            await apiService.deleteSavedMessage(item.id);
          } else if (item.type === 'snippet') {
            await apiService.deletePDFSnippet(item.id);
          } else if (item.type === 'studio-result') {
            const response = await savedContentService.deleteSavedContent(item.id);
            if (!response?.success) {
              errors.push(item.name || item.id);
            }
          }
        } catch {
          errors.push(item.name || item.id);
        }
      }

      clearLocalState();
      await refreshDriveData();

      if (errors.length > 0) {
        setBulkActionMessage(`Error al eliminar: ${errors.join(', ')}`);
      } else {
        setBulkActionMessage('Elementos eliminados');
      }
      setTimeout(() => setBulkActionMessage(null), 4000);
    } catch {
      setBulkActionMessage('Error al eliminar elementos');
      setTimeout(() => setBulkActionMessage(null), 4000);
    } finally {
      setIsBulkActionLoading(false);
    }
  }, [clearLocalState, deletableItems, onDocumentDelete, refreshDriveData]);

  const handleBulkReprocess = useCallback(async () => {
    if (!reprocessableItems.length) return;
    // Confirmación removida (window.confirm bloquea MCP automation)

    setIsBulkActionLoading(true);
    try {
      const processorPromises = reprocessableItems.map((item) =>
        apiService.reprocessDocument(item.data?.id)
      );
      await Promise.allSettled(processorPromises);
      setBulkActionMessage('Documentos reenviados para reprocesar');
      setTimeout(() => setBulkActionMessage(null), 4000);
    } catch {
      setBulkActionMessage('Error al reprocesar documentos');
      setTimeout(() => setBulkActionMessage(null), 4000);
    } finally {
      setIsBulkActionLoading(false);
    }
  }, [reprocessableItems]);

  return {
    handleBulkDelete,
    handleBulkReprocess,
    bulkActionMessage,
    isBulkActionLoading,
  };
};
